"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Menu, Search, X } from "lucide-react";
import type { HeaderData, LanguageOption } from "../../types";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import { RuntimeImage, RuntimeLink, useAssetUrl, useLocale } from "../../runtime/RendererRuntime";
import { GuardedLink, useDevPlaceholder } from "../../runtime/DevPlaceholder";
import { t } from "../../i18n/strings";
import { resolveLinkHref, resolveConfiguredHref } from "../../utils/links";
import { SiteHeaderMobile } from "./SiteHeaderMobile";
import { HeaderSearchForm } from "./HeaderSearchForm";
import { LanguageSelector } from "./LanguageSelector";
import { SocialBrandIcon } from "./footer/SocialBrandIcon";

/** Tuile continue (périodique sur les 2 axes) pour le filigrane de la rangée de nav. */
const NAV_SEAMLESS_KEY = "images/motif-bcc-bleu-seamless.png";

/** Site header component props. */
export interface SiteHeaderProps {
  data: HeaderData;
  logoSrc: string;
  logoAlt?: string;
  /** Fixed diamond icon. When set (with `logoWordmarkSrc`), the header renders
   *  icon + wordmark instead of the single combined `logoSrc`. */
  logoIconSrc?: string;
  /** Wordmark shown next to the icon — responsive (mobile + desktop, hidden on tablet). */
  logoWordmarkSrc?: string;
  homeHref?: string;
  language: string;
  languageOptions: LanguageOption[];
  defaultLanguageCode?: string;
  onLanguageChange?: (code: string) => void;
}

/** Site header with desktop/mobile navigation, search and language selector. */
export function SiteHeader({
  data,
  logoSrc,
  logoAlt = "Banque Centrale du Congo",
  logoIconSrc,
  logoWordmarkSrc,
  homeHref = "/",
  language,
  languageOptions,
  defaultLanguageCode,
  onLanguageChange,
}: SiteHeaderProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDesktopMenu, setOpenDesktopMenu] = useState<number | null>(null);
  const [menuLeft, setMenuLeft] = useState(0);
  const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined);
  // Gouttieres du conteneur, mesurees : le volet tablette les annule pour aller
  // d'un bord a l'autre de l'ecran.
  const [menuGutters, setMenuGutters] = useState({ left: 0, right: 0 });
  // Format TABLETTE du menu bureau : la rangee de nav est la, mais la colonne
  // photo du panneau est masquee (< 1024px). Le panneau y va d'un bord a l'autre.
  const [isTabletNav, setIsTabletNav] = useState(false);
  // En-tete NON epingle : globals.css libere `header.fixed` quand la hauteur utile
  // manque (< 600px, ou paysage < 850px) — l'en-tete redescend dans le flux. C'est
  // LE cas ou le panneau devient un volet qui pousse la page ; partout ailleurs
  // l'en-tete reste colle en haut et le panneau doit rester une surcouche.
  const [isHeaderUnpinned, setIsHeaderUnpinned] = useState(false);
  const [pointerLeft, setPointerLeft] = useState(24);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showDesktopSearch, setShowDesktopSearch] = useState(false);

  const { previewMode, notify } = useDevPlaceholder();
  const locale = useLocale();

  // In production, drop unconfigured (empty-href) links entirely; in preview, keep them so the
  // dev modal can explain they're not set up. Rules: a dropdown SECTION (an item defined with
  // children) is hidden entirely when none of its children resolve — even if the parent has its
  // own href — because an empty menu is worse than no menu (e.g. "Statistiques" with all-empty
  // children). A plain link (no children) survives only on its own configured href.
  const navItems = useMemo(() => {
    if (previewMode) return data.nav;
    return data.nav
      .filter((item) => {
        const rawChildren = item.children ?? [];
        if (rawChildren.length > 0) return rawChildren.some((child) => resolveConfiguredHref(child.href));
        return Boolean(resolveConfiguredHref(item.href));
      })
      .map((item) => ({ ...item, children: item.children?.filter((child) => resolveConfiguredHref(child.href)) }));
  }, [data.nav, previewMode]);

  // Conservé (inutilisé pour l'instant) : alimente le filigrane du header,
  // désactivé plus bas et gardé en commentaire pour restauration.
  const navSeamlessUrl = useAssetUrl(NAV_SEAMLESS_KEY);
  // Volet en flux = format sans photo ET en-tete desepingle. Les deux, jamais l'un
  // sans l'autre : sur un ecran haut, l'en-tete reste colle en haut, donc pousser
  // la page laisserait un trou sous un en-tete qui, lui, ne bouge pas.
  const menuInFlow = isTabletNav && isHeaderUnpinned;
  /** Meme regle que `menuInFlow`, lue en direct : utilisable avant que les effets
   *  aient synchronise les drapeaux (premier rendu, hydratation). */
  const menuInFlowNow = () =>
    typeof window !== "undefined" &&
    !window.matchMedia("(min-width: 1024px)").matches &&
    window.matchMedia("(max-height: 599px), (orientation: landscape) and (max-height: 849px)").matches;
  const socials = previewMode ? data.socials : (data.socials ?? []).filter((social) => resolveConfiguredHref(social.href));

  useBodyScrollLock(mobileOpen);

  // Deux conditions, deux media queries — celle de la photo (`lg:block`, 1024px)
  // et celle, mot pour mot, du mode compact de globals.css qui desepingle l'en-tete.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const large = window.matchMedia("(min-width: 1024px)");
    const compact = window.matchMedia("(max-height: 599px), (orientation: landscape) and (max-height: 849px)");
    const sync = () => {
      setIsTabletNav(!large.matches);
      setIsHeaderUnpinned(compact.matches);
    };
    sync();
    large.addEventListener("change", sync);
    compact.addEventListener("change", sync);
    return () => {
      large.removeEventListener("change", sync);
      compact.removeEventListener("change", sync);
    };
  }, []);

  // Close the mobile panel when the viewport grows past the desktop breakpoint —
  // otherwise rotating a tablet leaves an orphaned overlay with the scroll lock on.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(min-width: 768px)");
    const handleChange = (e: MediaQueryListEvent) => {
      if (e.matches) setMobileOpen(false);
    };
    mql.addEventListener("change", handleChange);
    return () => {
      mql.removeEventListener("change", handleChange);
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpenDesktopMenu(null);
        // Le panneau mobile se ferme aussi : sur écran bas sa barre de titre (donc
        // le bouton « Menu ») peut avoir défilé hors de vue.
        setMobileOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // The mega-menu panel is absolutely positioned against the nav container (one
  // shared panel, not one per item). NBB/ECB style: the wide panel is CENTERED in
  // the container (it spans most of it anyway), and only the little arrow pointer
  // tracks the trigger — that's why the pointer is computed independently.
  const openDesktopDropdown = (index: number, el: HTMLElement) => {
    setOpenDesktopMenu(index);
    if (!containerRef.current) return;
    const containerRect = containerRef.current.getBoundingClientRect();
    const triggerRect = el.getBoundingClientRect();
    // Le panneau se cale sur la COLONNE DE CONTENU (bord intérieur des gouttières
    // du conteneur), pas sur sa boîte de bordure : sinon, en tablette, il déborde
    // des items de nav de quelques pixels de chaque côté. Sur grand écran le
    // résultat est inchangé (colonne plus large que les 1200px du panneau).
    const style = window.getComputedStyle(containerRef.current);
    const padLeft = parseFloat(style.paddingLeft) || 0;
    const padRight = parseFloat(style.paddingRight) || 0;
    const contentWidth = Math.max(0, containerRect.width - padLeft - padRight);
    // Format TABLETTE (< 1024px, celui SANS la photo — voir `lg:block` sur la zone 3) :
    // le panneau est un volet EN FLUX sous la rangee de nav, donc rien a positionner ;
    // il s'etend d'un bord a l'autre par ses marges negatives et pousse la page vers
    // le bas (l'en-tete grandit, HeaderHeightSync recalcule le decalage).
    const viewportWidth = document.documentElement.clientWidth;
    const tablet = viewportWidth < 1024;
    const unpinned = window.matchMedia("(max-height: 599px), (orientation: landscape) and (max-height: 849px)").matches;
    setIsTabletNav(tablet);
    setIsHeaderUnpinned(unpinned);
    if (tablet && unpinned) {
      // Volet en flux : les gouttieres du conteneur sont MESUREES et non devinees
      // (elles changent avec le point de rupture), sinon un `-mx-*` en dur
      // deborderait de l'ecran.
      setMenuGutters({ left: padLeft, right: padRight });
      return;
    }
    // Sinon : surcouche. En tablette elle va d'un bord a l'autre de l'ecran, au
    // dela de 1024px elle se cale sur la colonne de contenu.
    const panelWidth = tablet ? viewportWidth : Math.min(1200, contentWidth);
    const clampedLeft = tablet ? -containerRect.left : padLeft + (contentWidth - panelWidth) / 2;
    setMenuLeft(clampedLeft);
    setMenuWidth(panelWidth);
    // Pointer under the CENTER of the trigger (the half-width shift compensates
    // the pointer's own 16px rotated square).
    const pointerPos = triggerRect.left - containerRect.left - clampedLeft + triggerRect.width / 2 - 8;
    setPointerLeft(Math.max(16, Math.min(panelWidth - 24, pointerPos)));
  };

  // Seul un REDIMENSIONNEMENT invalide les coordonnées calculées à l'ouverture
  // (largeur du conteneur). Le défilement ne les touche pas — l'en-tête est fixe,
  // le panneau défile avec lui — donc le menu reste ouvert pendant le scroll.
  useEffect(() => {
    const closeOnResize = () => setOpenDesktopMenu(null);
    window.addEventListener("resize", closeOnResize);
    return () => {
      window.removeEventListener("resize", closeOnResize);
    };
  }, []);

  // Clic/tap HORS du panneau et de la rangée de nav : referme le menu. Indispensable
  // sur tablette, où ni mouseleave ni Échap n'existent pour le fermer.
  useEffect(() => {
    if (openDesktopMenu === null) return;
    const onPointerDown = (e: PointerEvent) => {
      const container = containerRef.current;
      if (container && e.target instanceof Node && !container.contains(e.target)) {
        setOpenDesktopMenu(null);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [openDesktopMenu]);

  // The full inline search form only fits from 980px up; below that width
  // HeaderSearchForm collapses to a compact icon that links to /search.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(min-width: 980px)");
    const handleSearchToggle = (event?: MediaQueryListEvent | MediaQueryList) => {
      setShowDesktopSearch(event ? event.matches : mql.matches);
    };
    handleSearchToggle(mql);
    mql.addEventListener("change", handleSearchToggle);
    return () => {
      mql.removeEventListener("change", handleSearchToggle);
    };
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const defaultLangCode = defaultLanguageCode ?? languageOptions[0]?.code ?? language;
  const currentLanguage =
    languageOptions.find((opt) => opt.code === language) ??
    languageOptions.find((opt) => opt.code === defaultLangCode) ??
    languageOptions[0];

  const handleLanguageChange = (code: string) => {
    onLanguageChange?.(code);
  };

  return (
    <header className={`fixed inset-x-0 top-0 z-[45] bg-white ${isScrolled && !isHeaderUnpinned ? "shadow-[0_4px_10px_rgba(0,0,0,0.25)]" : "shadow-none"} transition-shadow duration-200`}>
      {/* DÉSACTIVÉ (conservé pour restauration) — filigrane losanges UNIQUE et
          CONTINU sur toute la hauteur du header (bandeau supérieur + rangée de
          nav), un seul dégradé : le <header> (fixed z-[45]) forme un contexte
          d'empilement, donc ce calque -z-10 peint au-dessus de son fond blanc et
          sous tout le contenu. Les barres internes n'ont pas de fond propre (il
          recouvrirait le motif) — les rétablir n'est PAS nécessaire pour
          réactiver. Les filigranes du méga-menu et du pied de page restent actifs.
      {navSeamlessUrl ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -z-10 opacity-[0.10]"
          style={{
            backgroundImage: `url(${navSeamlessUrl})`,
            backgroundRepeat: "repeat",
            backgroundPosition: "left bottom",
            backgroundSize: "auto 36px",
            WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.5) 55%, rgba(0,0,0,0.75) 95%)",
            maskImage: "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.5) 55%, rgba(0,0,0,0.75) 95%)",
          }}
        />
      ) : null}
      */}
      {/* Lien d'évitement (WCAG 2.4.1) : premier élément focusable de la page, visible
          uniquement au focus clavier, saute directement au <main id="contenu">. */}
      <a
        href="#contenu"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[60] focus:rounded-md focus:bg-[#005CA9] focus:px-4 focus:py-2 focus:font-semibold focus:text-white"
      >
        {t(locale, "skipToContent")}
      </a>
      {/* Fond porté par le <header> (blanc opaque) : pas de fond propre ici,
          il recouvrirait le filigrane continu du header. */}
      <div className="border-b border-[#005ca92e]">
        <div className="bcc-header-row relative mx-auto max-w-7xl px-4 md:px-8 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 md:gap-2 min-[900px]:gap-4 py-2 sm:py-3 text-sm xl:text-base min-h-[70px] xl:min-h-[78px]" onMouseEnter={() => { if (!menuInFlowNow()) setOpenDesktopMenu(null); }}>
          <div className="flex items-center gap-3 sm:gap-4 md:gap-2 min-[900px]:gap-5 lg:gap-6 min-w-0">
            <RuntimeLink
              href={homeHref}
              className="flex items-center gap-2.5 font-semibold min-h-0 min-w-0 pr-2 md:pr-1 min-[900px]:pr-2"
              aria-label={logoAlt}
              style={{
                flex: "1 1 540px",
                maxWidth: "540px",
                minWidth: 0,
                minHeight: 0,
                height: "clamp(42px, 8vw, 48px)",
              }}
            >
              {logoIconSrc && logoWordmarkSrc ? (
                <>
                  {/* Fixed diamond mark — always visible. */}
                  <RuntimeImage
                    src={logoIconSrc}
                    alt={logoAlt}
                    className="h-full w-auto max-h-[48px] shrink-0 object-contain"
                  />
                  {/* Wordmark — 12px de haut à TOUTES les tailles (mobile, tablette, bureau),
                      comme demandé par le client. L'image est rognée au plus près (aucune marge),
                      donc une hauteur FIXE de 12px rend le texte à la bonne taille ; `max-w-full`
                      ne fait que le rétrécir sur les écrans très étroits. */}
                  <RuntimeImage
                    src={logoWordmarkSrc}
                    alt=""
                    aria-hidden="true"
                    className="block h-[12px] w-auto max-w-full min-w-0 object-contain object-left"
                  />
                </>
              ) : (
                <RuntimeImage
                  src={logoSrc}
                  alt={logoAlt}
                  className="h-full w-auto max-h-[44px] max-w-full min-w-0 min-h-0 object-contain transition-all duration-200"
                  style={{ maxWidth: "340px" }}
                />
              )}
              <span className="sr-only">BCC</span>
            </RuntimeLink>
            <nav
              className="hidden min-[765px]:flex flex-nowrap items-center gap-6 ml-auto px-[22px] min-[765px]:px-2 min-[900px]:px-[22px] whitespace-nowrap shrink-0"
              style={{ fontFamily: "var(--font-nunito)" }}
            >
              {data.quickLinks.map((link) => (
                <GuardedLink
                  key={link.label}
                  label={link.label}
                  className="text-base xl:text-lg font-semibold text-[#005CA9] hover:text-[#005CA9]"
                  href={link.href}
                >
                  {link.label}
                </GuardedLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-3 sm:gap-4 min-[765px]:gap-6 justify-self-end">
            <HeaderSearchForm showForm={showDesktopSearch} />
            <LanguageSelector
              variant="desktop"
              language={language}
              languageOptions={languageOptions}
              currentLanguage={currentLanguage}
              onLanguageChange={handleLanguageChange}
            />
            {/* Pastille recherche mobile — même format que le bouton menu, accès direct
                à la page de recherche (la barre mobile n'a pas de formulaire inline). */}
            <RuntimeLink
              href="/search"
              className="inline-flex items-center rounded-md border border-[#005CA9]/15 bg-[#F4F8FC] px-3 py-2 min-[765px]:hidden text-[#005CA9] shrink-0 transition-colors hover:bg-[#EAF2F9]"
              aria-label={t(locale, "openSearch")}
            >
              <Search className="h-5 w-5" />
              <span className="sr-only">{t(locale, "openSearch")}</span>
            </RuntimeLink>
            {/* Soft tint + 15% blue border — secondary-button weight, matching the mobile
                menu's other pills. The previous solid-blue border read as a primary action. */}
            <button className="inline-flex items-center rounded-md border border-[#005CA9]/15 bg-[#F4F8FC] px-3 py-2 min-[765px]:hidden text-[#005CA9] shrink-0 transition-colors hover:bg-[#EAF2F9]" aria-expanded={mobileOpen} aria-controls="mobile-menu" onClick={() => setMobileOpen((v) => !v)}>
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              <span className="sr-only">{t(locale, "openCloseMenu")}</span>
            </button>
          </div>
        </div>
      </div>
      {/* 765px+: bottom border on the nav row provides the separator between header and the
          below-header slot. Mobile keeps no border here so the (empty) wrapper stays invisible. */}
      {/* Fond porté par le <header> — voir le filigrane continu ci-dessus. */}
      <div className="min-[765px]:border-b min-[765px]:border-[#005ca92e]">
        <div ref={containerRef} className="bcc-header-row relative mx-auto max-w-7xl px-4 md:px-8 ">
          {/* La bascule bureau ↔ mobile de l'EN-TÊTE se fait à 765px, alors que le reste du
              site garde son md: à 700px (globals.css) : entre 700 et 765 la rangée de nav
              débordait. L'écart horizontal est resserré sur la plage tablette (765 → lg)
              puis revient à gap-x-6 sur les grands écrans. */}
          <nav className="relative hidden items-center gap-x-4 lg:gap-x-6 gap-y-3 text-sm xl:text-base min-[765px]:flex py-[22px]">
            {/* Main nav items first; socials are appended after via ml-auto wrapper. */}
            {navItems.map((item, i) => {
              const itemHref = resolveConfiguredHref(item.href);
              const hasChildren = (item.children?.length ?? 0) > 0;
              return (
                <div key={item.label} data-nav-item className="relative" onMouseLeave={(e) => {
                  if (menuInFlowNow()) return;
                  const related = (e as unknown as React.MouseEvent<HTMLDivElement> & { relatedTarget: Node | null }).relatedTarget as Node | null;
                  const container = containerRef.current;
                  if (container && related && container.contains(related)) return;
                  setOpenDesktopMenu(null);
                }}>
                  {hasChildren && itemHref ? (
                    // Section à enfants AVEC page propre : le LIBELLÉ y navigue et le CHEVRON
                    // ouvre le panneau — deux cibles distinctes. Un bouton unique interdisait
                    // d'atteindre la page de section ; un lien unique rendait le panneau
                    // inatteignable en tablette (pas de survol, le premier tap naviguait).
                    <span className="flex items-center gap-1">
                      <RuntimeLink
                        href={itemHref}
                        className="text-base xl:text-lg max-[1050px]:text-sm flex items-center font-semibold whitespace-nowrap text-[#005CA9] hover:text-[#005CA9] hover:underline"
                        onClick={() => setOpenDesktopMenu(null)}
                        onMouseEnter={(e) => {
                          if (menuInFlowNow()) return;
                          const group = (e.currentTarget as HTMLElement).closest("[data-nav-item]") as HTMLElement | null;
                          if (group) openDesktopDropdown(i, group);
                        }}
                      >
                        {item.label}
                      </RuntimeLink>
                      <button
                        type="button"
                        className="flex items-center text-[#0095DB]"
                        aria-haspopup="menu"
                        aria-expanded={openDesktopMenu === i}
                        aria-label={`${item.label} : ${t(locale, openDesktopMenu === i ? "collapse" : "expand")}`}
                        onClick={(e) => {
                          if (openDesktopMenu === i) {
                            setOpenDesktopMenu(null);
                            return;
                          }
                          const group = (e.currentTarget as HTMLElement).closest("[data-nav-item]") as HTMLElement | null;
                          openDesktopDropdown(i, group ?? e.currentTarget);
                        }}
                        onMouseEnter={(e) => {
                          if (menuInFlowNow()) return;
                          const group = (e.currentTarget as HTMLElement).closest("[data-nav-item]") as HTMLElement | null;
                          openDesktopDropdown(i, group ?? e.currentTarget);
                        }}
                      >
                        <ChevronDown className={`h-4 w-4 transition-transform ${openDesktopMenu === i ? "rotate-180" : ""}`} />
                      </button>
                    </span>
                  ) : hasChildren ? (
                    // Section sans page propre : le libellé reste la bascule du panneau.
                    <button
                      type="button"
                      className={`text-base xl:text-lg max-[1050px]:text-sm flex items-center font-semibold gap-1 whitespace-nowrap text-[#005CA9] hover:text-[#005CA9] ${openDesktopMenu === i ? "text-[#005CA9]" : ""}`}
                      aria-haspopup="menu"
                      aria-expanded={openDesktopMenu === i}
                      onClick={(e) => {
                        if (openDesktopMenu === i) {
                          setOpenDesktopMenu(null);
                        } else {
                          openDesktopDropdown(i, e.currentTarget);
                        }
                      }}
                      onMouseEnter={(e) => { if (!menuInFlowNow()) openDesktopDropdown(i, e.currentTarget); }}
                    >
                      {item.label} <ChevronDown className={`h-4 w-4 text-[#0095DB] transition-transform ${openDesktopMenu === i ? "rotate-180" : ""}`} />
                    </button>
                  ) : itemHref ? (
                    <RuntimeLink
                      href={itemHref}
                      className="text-base xl:text-lg max-[1050px]:text-sm flex items-center font-semibold gap-1 whitespace-nowrap text-[#005CA9] hover:text-[#005CA9]"
                      onClick={() => setOpenDesktopMenu(null)}
                    >
                      {item.label}
                    </RuntimeLink>
                  ) : (
                    // Empty leaf (no href, no children) — preview only (filtered out in production):
                    // clicking opens the "not configured yet" dev modal instead of going to the home page.
                    <button
                      type="button"
                      data-dev-placeholder="true"
                      className="text-base xl:text-lg max-[1050px]:text-sm flex items-center font-semibold gap-1 whitespace-nowrap text-[#005CA9] hover:text-[#005CA9] cursor-pointer"
                      onClick={() => notify(item.label)}
                    >
                      {item.label}
                    </button>
                  )}
                </div>
              );
            })}
            {/* Social shortcuts pulled from siteSettings.header.socials. Yellow circular pills,
                CMS-driven so editors control which networks are exposed and in what order.
                Hidden below 1000px so the nav row stays clean on narrower viewports. */}
            {socials && socials.length > 0 && (
              <div className="ml-auto hidden min-[980px]:flex items-center gap-2">
                {socials.map((social) => (
                  <a
                    key={social.label}
                    href={resolveLinkHref(social.href) ?? "#"}
                    aria-label={social.label}
                    target={social.icon === "mail" ? undefined : "_blank"}
                    rel={social.icon === "mail" ? undefined : "noreferrer"}
                    className="bcc-header-social grid h-9 w-9 place-items-center rounded-full bg-[#FFA401] text-white transition-opacity hover:opacity-90"
                  >
                    <SocialBrandIcon type={social.icon} />
                  </a>
                ))}
              </div>
            )}
          </nav>
          {openDesktopMenu !== null && navItems[openDesktopMenu] && (navItems[openDesktopMenu].children?.length ?? 0) > 0 && (() => {
            const openItem = navItems[openDesktopMenu];
            const introHref = resolveConfiguredHref(openItem.href);
            // Zone intro rendue dès qu'il y a une description OU une cible « Vue
            // d'ensemble » ; zone photo seulement si une image est configurée. Un
            // layout non enrichi retombe donc sur la simple liste de liens.
            const hasImage = Boolean((openItem.image?.src ?? "").trim());
            // Répartition en colonnes : chaque intertitre ouvre un groupe ; un groupe dont
            // l'entrée porteuse a `introColumn` s'affiche dans la colonne intro (sous le
            // bouton « Vue d'ensemble ») pour désengorger la liste centrale. Le menu
            // mobile ignore ce drapeau et garde l'ordre à plat.
            const allChildren = openItem.children ?? [];
            const introChildren: typeof allChildren = [];
            const listChildren: typeof allChildren = [];
            let currentIntro = false;
            for (const child of allChildren) {
              if (child.heading) currentIntro = Boolean(child.introColumn);
              (currentIntro ? introChildren : listChildren).push(child);
            }
            // La colonne intro se rend aussi quand elle ne porte QUE des groupes rapatriés
            // (sinon leurs liens disparaîtraient sur un layout sans description).
            const hasIntro =
              Boolean((openItem.description ?? "").trim()) || Boolean(introHref) || introChildren.length > 0;
            return (
            <div
              className={
                menuInFlow
                  ? // Volet en flux (en-tete non epingle) : bord a bord et a sa hauteur
                    // NATURELLE. Pas d'ascenseur propre — l'en-tete defile deja avec la
                    // page, un second ascenseur ferait doublon et decrochait le filigrane
                    // (un `bottom-0` dans un conteneur defilant ne suit pas le contenu).
                    "relative z-50 overflow-hidden border-t border-black/5 bg-white"
                  : // Surcouche : elle flotte AU-DESSUS du contenu, elle garde donc son
                    // ombre portee — y compris en bord a bord, ou seule la retombee basse
                    // se voit (les cotes sortent de l'ecran). Seul le volet en flux s'en
                    // passe : la page est dessous, il n'y a rien a detacher.
                    `absolute top-full z-50 mt-0 w-[min(1200px,calc(100vw-2rem))] overflow-hidden bg-white shadow-xl ring-1 ring-black/10 ${isTabletNav ? "rounded-none" : "rounded-md"}`
              }
              style={menuInFlow ? { marginLeft: -menuGutters.left, marginRight: -menuGutters.right } : { left: menuLeft, width: menuWidth }}
              onMouseEnter={() => { }}
              onMouseLeave={() => { if (!menuInFlowNow()) setOpenDesktopMenu(null); }}
            >
              {/* La fleche ne vaut que pour la surcouche : en flux, le volet touche deja la nav. */}
              {menuInFlow ? null : (
                <span className="pointer-events-none absolute -top-2 block h-4 w-4 rotate-45 rounded-sm bg-white ring-1 ring-black/10" style={{ left: pointerLeft }} />
              )}
              <div className="flex gap-2 p-6 xl:p-8">
                {/* Zone 1 — intro de section : titre serif bleu nuit, description, bouton
                    « Vue d'ensemble » (équivalent du « En savoir plus » NBB). */}
                {hasIntro && (
                  <div className="w-[30%] shrink-0 border-r border-black/5 pr-6 xl:pr-8">
                    {/* Format sans photo : titre sur UNE ligne (ellipse si besoin) et
                        corps reduit — sinon un titre a deux lignes decale tout ce qui
                        suit, et « Services au public » ne tombe plus au meme niveau
                        d'une section a l'autre. */}
                    <p className="max-[1023px]:truncate max-[1023px]:text-[19px] text-[24px] xl:text-[26px] font-bold leading-tight text-[#0F2849]" style={{ fontFamily: "var(--font-aleo)" }}>
                      {openItem.label}
                    </p>
                    {(openItem.description ?? "").trim() ? (
                      <p className="mt-3 text-[14px] xl:text-[15px] leading-relaxed text-[#4B5E7E] max-[1023px]:line-clamp-3 max-[1023px]:min-h-[69px]">
                        {openItem.description}
                      </p>
                    ) : null}
                    {introHref ? (
                      <GuardedLink
                        label={openItem.label}
                        href={openItem.href}
                        onClick={() => setOpenDesktopMenu(null)}
                        className="mt-5 inline-flex items-center gap-2 rounded-md bg-[#005CA9] px-4 py-2.5 text-[14px] xl:text-[15px] font-semibold text-white transition-colors hover:bg-[#0a4a8a]"
                      >
                        {t(locale, "menuOverview")}
                        <ChevronRight className="h-4 w-4 shrink-0 text-[#FFA401]" aria-hidden="true" />
                      </GuardedLink>
                    ) : null}
                    {/* Groupes rapatriés dans la colonne intro (introColumn) : liste
                        compacte façon « Liens rapides » NBB, sous le bouton. Ils restent
                        dans cette colonne à toutes les tailles, y compris sur le
                        format sans photo. */}
                    {introChildren.length > 0 && (
                      <ul className="mt-9">
                        {introChildren.map((child) => (
                          <li key={child.label}>
                            {child.heading ? (
                              <span className="mb-1.5 mt-4 block max-[1050px]:text-[11px] text-[12px] font-semibold uppercase tracking-[0.08em] text-[#6B7A99]">
                                {child.heading}
                              </span>
                            ) : null}
                            {/* Même style que la liste centrale (graisse, corps, police) —
                                seule la structure de rangée (filets, fond au survol) reste propre
                                à la colonne intro, plus étroite. */}
                            <GuardedLink
                              label={child.label}
                              href={child.href}
                              onClick={() => setOpenDesktopMenu(null)}
                              className="group flex items-center gap-2.5 py-2 text-left transition-colors hover:text-[#0a4a8a]"
                            >
                              <span
                                className="block max-w-full overflow-hidden text-ellipsis whitespace-nowrap max-[1050px]:text-[14px] text-[16px] xl:text-[17px] font-bold leading-normal text-[#005CA9]"
                                style={{ fontFamily: 'var(--font-nunito, "Nunito Sans"), "Nunito Sans", system-ui, -apple-system, "Segoe UI", sans-serif', fontStyle: "normal" }}
                              >
                                {child.label}
                              </span>
                            </GuardedLink>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                {/* Zone 2 — liste des liens : rangées aérées séparées par des filets,
                    intertitres de groupe conservés. */}
                <ul className="min-w-0 flex-1 self-start px-2">
                  {listChildren.map((child, childIndex) => {
                    return (
                      <li key={child.label}>
                        {/* Intertitre de groupe (facultatif) : rendu seulement si l'entrée en
                            porte un. La respiration au-dessus se pilote par l'index, PAS par
                            `first:` (l'intertitre est toujours le premier enfant de son <li>). */}
                        {child.heading ? (
                          <span
                            className={`${childIndex === 0 ? "mt-0" : "mt-5"} mb-1 block px-3 max-[1050px]:text-[11px] text-[12px] font-semibold uppercase tracking-[0.08em] text-[#6B7A99]`}
                          >
                            {child.heading}
                          </span>
                        ) : null}
                        {/* Filet au-dessus de chaque rangée sans intertitre (sauf la première) :
                            même rythme que les listes NBB. */}
                        {/* Pas de chevron en fin de rangee : il laissait croire qu'un sous-menu
                            allait s'ouvrir alors que la rangee est un simple lien (demande 58). */}
                        <GuardedLink
                          label={child.label}
                          className={`group relative flex w-full max-w-full items-center gap-2.5 rounded-md px-3 py-2.5 text-left transition-colors hover:bg-[#0a4a8a]/8 ${childIndex > 0 && !child.heading ? "border-t border-black/5" : ""}`}
                          href={child.href}
                          onClick={() => setOpenDesktopMenu(null)}
                        >
                          <span
                            className="block max-w-full overflow-hidden text-ellipsis whitespace-nowrap max-[1050px]:text-[14px] text-[16px] xl:text-[17px] font-bold leading-normal text-[#005CA9]"
                            style={{ fontFamily: 'var(--font-nunito, "Nunito Sans"), "Nunito Sans", system-ui, -apple-system, "Segoe UI", sans-serif', fontStyle: "normal" }}
                          >
                            {child.label}
                          </span>
                        </GuardedLink>
                      </li>
                    );
                  })}
                </ul>
                {/* Zone 3 — photo du stock (facultative). Le panneau occupe désormais toute
                    la largeur du conteneur (1200px) : la photo prend un bon tiers. */}
                {hasImage && (
                  <div className="hidden w-[32%] shrink-0 lg:block">
                    <RuntimeImage
                      src={openItem.image!.src}
                      alt={openItem.image!.alt || ""}
                      className="h-full max-h-[440px] w-full rounded-md object-cover"
                    />
                  </div>
                )}
              </div>
            </div>
            );
          })()}
        </div>
      </div>

      {mobileOpen && (
        <SiteHeaderMobile
          data={data}
          logoSrc={logoSrc}
          logoIconSrc={logoIconSrc}
          logoWordmarkSrc={logoWordmarkSrc}
          logoAlt={logoAlt}
          homeHref={homeHref}
          language={language}
          languageOptions={languageOptions}
          currentLanguage={currentLanguage}
          onLanguageChange={handleLanguageChange}
          onClose={() => setMobileOpen(false)}
        />
      )}
    </header>
  );
}
